import { FxRateService } from '@/services/FxRateService';
import type { Budget, Transaction } from '@/types';

export function toDateOnly(value: string) {
  return new Date(`${value.split('T')[0]}T00:00:00`);
}

export function toIsoDate(date: Date) {
  return date.toISOString().split('T')[0];
}

export function buildDateRange(startDate: string, endDate: string) {
  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate);
  const dates: string[] = [];

  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(toIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

export function clipDateRange(
  startDate: string,
  endDate: string,
  clipStart?: string,
  clipEnd?: string
): { startDate: string; endDate: string } | null {
  const start = toDateOnly(clipStart && clipStart > startDate ? clipStart : startDate);
  const end = toDateOnly(clipEnd && clipEnd < endDate ? clipEnd : endDate);
  if (start > end) return null;
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
}

async function convertAmount(
  amount: number,
  from: string,
  to: string,
  date: string
): Promise<number> {
  if (from.toUpperCase() === to.toUpperCase()) return amount;
  const result = await FxRateService.convert(amount, from, to, date.split('T')[0]);
  return result.convertedAmount;
}

export async function convertTransactionsToCurrency(
  transactions: Transaction[],
  targetCurrency: string
): Promise<Map<string, number>> {
  const converted = new Map<string, number>();
  const rateCache = new Map<string, number>();
  const target = targetCurrency.toUpperCase();

  const uniquePairs = new Map<string, { from: string; date: string }>();
  for (const tx of transactions) {
    const date = tx.date.split('T')[0];
    if (tx.currency.toUpperCase() === target) continue;
    const key = `${tx.currency.toUpperCase()}_${date}`;
    uniquePairs.set(key, { from: tx.currency, date });
  }

  await Promise.all(
    Array.from(uniquePairs.entries()).map(async ([key, { from, date }]) => {
      const result = await FxRateService.convert(1, from, targetCurrency, date);
      rateCache.set(key, result.rate);
    })
  );

  for (const tx of transactions) {
    const date = tx.date.split('T')[0];
    if (tx.currency.toUpperCase() === target) {
      converted.set(tx.id, tx.amount);
      continue;
    }
    const key = `${tx.currency.toUpperCase()}_${date}`;
    const rate = rateCache.get(key);
    if (rate === undefined) {
      const result = await FxRateService.convert(tx.amount, tx.currency, targetCurrency, date);
      converted.set(tx.id, result.convertedAmount);
      continue;
    }
    converted.set(tx.id, tx.amount * rate);
  }

  return converted;
}

export interface BurndownSeries {
  budgetId: string;
  budgetName: string;
  currency: string;
  originalCurrency: string;
  budgetAmount: number;
  originalBudgetAmount: number;
  totalSpent: number;
  remaining: number;
  dayLabels: string[];
  remainingByDay: (number | null)[];
  periodLabel: string;
  color: string;
}

export const SERIES_COLORS = [
  'hsl(174 72% 40%)',
  'hsl(239 68% 58%)',
  'hsl(38 92% 50%)',
  'hsl(0 72% 51%)',
  'hsl(271 68% 58%)',
  'hsl(330 70% 55%)',
];

export function buildBurndownSeries(
  budget: Budget,
  transactions: Transaction[],
  convertedAmounts: Map<string, number>,
  convertedBudgetAmount: number,
  startDate: string,
  endDate: string,
  displayCurrency: string,
  color: string
): BurndownSeries {
  const dateRange = buildDateRange(startDate, endDate);
  const spendByDate = new Map<string, number>();

  transactions.forEach((tx) => {
    const date = toIsoDate(toDateOnly(tx.date));
    if (date < startDate || date > endDate) return;
    const amount = convertedAmounts.get(tx.id) ?? tx.amount;
    spendByDate.set(date, (spendByDate.get(date) ?? 0) + amount);
  });

  let cumulative = 0;
  const remainingByDay: number[] = [];

  dateRange.forEach((date) => {
    cumulative += spendByDate.get(date) ?? 0;
    remainingByDay.push(convertedBudgetAmount - cumulative);
  });

  const totalSpent = cumulative;
  const remaining = convertedBudgetAmount - totalSpent;

  return {
    budgetId: budget.id,
    budgetName: budget.name,
    currency: displayCurrency,
    originalCurrency: budget.currency,
    budgetAmount: convertedBudgetAmount,
    originalBudgetAmount: budget.amount,
    totalSpent,
    remaining,
    dayLabels: dateRange.map((_, index) => `Day ${index + 1}`),
    remainingByDay,
    periodLabel: `${startDate} – ${endDate}`,
    color,
  };
}

export async function buildAnalyticsSeries(
  budgets: Budget[],
  transactionsByBudget: Map<string, Transaction[]>,
  displayCurrency: string,
  clipStart?: string,
  clipEnd?: string
): Promise<BurndownSeries[]> {
  const series: BurndownSeries[] = [];

  for (let index = 0; index < budgets.length; index++) {
    const budget = budgets[index]!;
    const clipped = clipDateRange(budget.startDate, budget.endDate, clipStart, clipEnd);
    if (!clipped) continue;

    const transactions = transactionsByBudget.get(budget.id) ?? [];
    const convertedAmounts = await convertTransactionsToCurrency(transactions, displayCurrency);
    const convertedBudgetAmount = await convertAmount(
      budget.amount,
      budget.currency,
      displayCurrency,
      clipped.startDate
    );

    series.push(
      buildBurndownSeries(
        budget,
        transactions,
        convertedAmounts,
        convertedBudgetAmount,
        clipped.startDate,
        clipped.endDate,
        displayCurrency,
        SERIES_COLORS[index % SERIES_COLORS.length]!
      )
    );
  }

  return series;
}

export function mergeSeriesForChart(series: BurndownSeries[]) {
  const maxDays = Math.max(0, ...series.map((s) => s.remainingByDay.length));

  return Array.from({ length: maxDays }, (_, dayIndex) => {
    const point: Record<string, string | number | null> = {
      day: `Day ${dayIndex + 1}`,
    };

    series.forEach((s) => {
      point[s.budgetId] = s.remainingByDay[dayIndex] ?? null;
    });

    return point;
  });
}

export function resolveDisplayCurrency(
  selectedBudgets: Budget[],
  choice: string
): { currency: string; warning?: string } {
  if (choice && choice !== 'AUTO') {
    return { currency: choice };
  }

  const currencies = new Set(selectedBudgets.map((b) => b.currency.toUpperCase()));
  if (currencies.size === 1) {
    return { currency: selectedBudgets[0]!.currency };
  }

  return {
    currency: 'USD',
    warning: 'Selected budgets use different currencies. Showing values in USD.',
  };
}

export function collectCurrencyOptions(budgets: Budget[], common: string[]): string[] {
  const fromBudgets = budgets.map((b) => b.currency.toUpperCase());
  return Array.from(new Set([...common, ...fromBudgets])).sort();
}
